<template>
    <div class="container-fluid px-0">

        <AuthNav></AuthNav>

        <NavButtons light-btns></NavButtons>

    </div>

    <div class="row">
        <div v-if="!passwordResetDone" ref="resetPasswordForm" class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 mb-3">
            <div class="d-flex flex-column gap-3 text-center mb-5">
                <h3 class="fw-semi-bold m-0">Reset Password</h3>
                <p class="text-secondary m-0">Use the form below to reset your Account Password.</p>
            </div>
            <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken my-3 bg-input-disabled" :class="formErrors.email ? 'border-danger' : 'border-secondary'">
                <label for="email">
                    <i class="me-3 dd-icon bi bi-envelope-at"></i>
                </label>
                <input v-model="email" :readonly="true" :disabled="true" type="email" id="email" name="email" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Email Address" @input="removeFormErrorKey('email')" >
            </div>
            <div v-if="formErrors.email" class="validation-error" v-html="formErrors.email"></div>

            <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken my-3" :class="formErrors.password ? 'border-danger' : 'border-secondary'">
                <label for="password">
                    <i class="me-3 dd-icon bi bi-shield-lock"></i>
                </label>
                <input v-model="password" :type="passwordToggle ? 'text' : 'password'" id="password" name="password" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Password" @input="removePassErrorKeys" >
                <span class="rounded-circle" v-ripple>
                    <i @click="(event) => passwordToggle = !passwordToggle" :class="passwordToggle ? 'bi-eye' : 'bi-eye-slash'" v-ripple></i>
                </span>
            </div>
            <div v-if="formErrors.password" class="validation-error" v-html="formErrors.password"></div>

            <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken my-3" :class="formErrors.password_confirmation ? 'border-danger' : 'border-secondary'">
                <label for="password_confirmation">
                    <i class="me-3 dd-icon bi bi-shield-check"></i>
                </label>
                <input v-model="password_confirmation" :type="confirmPasswordToggle ? 'text' : 'password'" id="password_confirmation" name="password_confirmation" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Confirm Password" @input="removePassErrorKeys" >
                <span class="rounded-circle" v-ripple>
                    <i @click="(event) => confirmPasswordToggle = !confirmPasswordToggle" :class="confirmPasswordToggle ? 'bi-eye' : 'bi-eye-slash'"></i>
                </span>
            </div>
            <div v-if="formErrors.password_confirmation" class="validation-error" v-html="formErrors.password_confirmation"></div>

            <div class="d-flex flex-column align-items-center justify-content-between text-center mt-3">
                <button class="btn btn-dark rounded-pill d-flex align-items-center justify-content-center py-2 px-3 w-50" @click="resetPassword" :disabled="hasFormErrors">
                    Set Password <i class="bi bi-arrow-right"></i>
                </button>

                <div class="mt-3">
                    <span class="text-secondary">Don't want to change Password? Go Back to </span>
                    <a v-load-page="'/login'" class="text-primary cursor-pointer">Login</a>
                </div>
            </div>
        </div>
        <div v-else class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 mb-3">
            <div class="d-flex flex-column gap-3 text-center mb-4">
                <h3 class="fw-semi-bold m-0">Password Changed!</h3>
                <p class="text-secondary m-0" v-html="responseMessage"></p>
            </div>
            <div class="text-center">
                <span class="text-secondary">Click here to </span>
                <a v-load-page="'/login'" class="text-primary cursor-pointer">Login</a>
            </div>
        </div>

    </div>

</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useAuthStore} from "@/stores/auth-store";
import {useAppHelpers} from "@/composables/useAppHelpers";
import {useHttpErrorManager} from "@/composables/useHttpErrorManager";
import NavButtons from "@/components/NavButtons.vue";
import AuthNav from "@/views/Auth/AuthNav.vue";
import apiClient from "@/utils/axios";
import {useRouter} from "vue-router";

const props = defineProps({
    token: String,
    email: String,
});

// Initialize necessary stores and helpers
const auth = useAuthStore();
const helper = useAppHelpers();
const router = useRouter();

const resetPasswordForm = ref(null);
const passwordResetDone = ref(false);
const responseMessage = ref('');

// Define reactive references for form fields and states
const email = ref(props.email ? props.email : '');
const password = ref('');
const password_confirmation = ref('');

const passwordToggle = ref(false);
const confirmPasswordToggle = ref(false);
const formErrors = ref({});

onMounted(async () => {
    if (!props.email || !props.token) {
        router.push({ name: 'home' });
    }
});

// Function to validate password and password confirmation
const validatePassword = () => {
    if (!password.value) {
        // helper.toast('Please enter your password to continue.', 'error');
        formErrors.value.password = 'Please enter your password to continue.';
        return false;
    }
    if (password.value.length < 8) {
        // helper.toast('Password must be at least 8 characters long.', 'error');
        formErrors.value.password = 'Password must be at least 8 characters long.';
        return false;
    }

    const number = /[0-9]/g;
    const upperCase = /[A-Z]/g;
    const specialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;

    if (!password.value.match(upperCase)) {
        // helper.toast('Password must contain at least one uppercase letter.', 'error');
        formErrors.value.password = 'Password must contain at least one uppercase letter.';
        return false;
    }
    if (!password.value.match(number)) {
        // helper.toast('Password must contain at least one number.', 'error');
        formErrors.value.password = 'Password must contain at least one number.';
        return false;
    }
    if (!password.value.match(specialChar)) {
        // helper.toast('Password must contain at least one special character.', 'error');
        formErrors.value.password = 'Password must contain at least one special character.';
        return false;
    }
    if (!password_confirmation.value) {
        // helper.toast('Please confirm your password to continue.', 'error');
        formErrors.value.password_confirmation = 'Please confirm your password.';
        return false;
    }
    if (password.value !== password_confirmation.value) {
        // helper.toast('Passwords do not match.', 'error');
        formErrors.value.password = "Password and it's confirmation do not match.";
        return false;
    }
    return true;
};

const validateEmail = () => {
    if (!email.value || email.value.trim() === "") {
        // helper.toast('Please enter your email address to continue.', 'error');
        formErrors.value.email = 'Please enter your email address to continue.';
        return false;
    }

    let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    let check = re.test(email.value);
    if (!check) {
        // helper.toast('Please enter a valid email address.', 'error');
        formErrors.value.email = 'Please enter a valid email address.';
    }

    return check;
}

// Function to handle user registration
const resetPassword = async () => {
    helper.loaderOn(resetPasswordForm.value);
    if (!validateEmail()) {
        helper.loaderOff(resetPasswordForm.value);
        return;
    }
    if (!validatePassword()) {
        helper.loaderOff(resetPasswordForm.value);
        return;
    }

    try {
        const response = await apiClient.post('/reset-password', {
            email: email.value,
            password: password.value,
            password_confirmation: password_confirmation.value,
            token: props.token
        });
        helper.loaderOff(resetPasswordForm.value);
        if(response.status === 200){
            passwordResetDone.value = true;
            responseMessage.value = response.data.message;
        }
    } catch (error) {
        helper.loaderOff(resetPasswordForm.value);
        let errorObj = await useHttpErrorManager().handleError(error);
        if (error.response.status === 422) {
            formErrors.value = errorObj;
	        removeFormErrorKey('full_message');
            removeFormErrorKey('alt_message');
            removeFormErrorKey('err_message');
        }
        // helper.toast(error.response.data.message, 'error');
    }
};

const removePassErrorKeys = () => {
    removeFormErrorKey('password');
    removeFormErrorKey('password_confirmation');
};

const removeFormErrorKey = (key) => {
    delete formErrors.value[key];
};

const hasFormErrors = computed(() => {
    return Object.keys(formErrors.value).length > 0;
});

</script>

<style scoped lang="scss"></style>
